import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { filterBundleParentsFromPurchasables, getBundleParentFromLineItems } from "@smartrr/shared/utils/bundles";
import { shopifyGidToNumber } from "@smartrr/shared/utils/ensureShopifyGid";
import { isVariantInStock } from "@smartrr/shared/utils/variants";
import flatten from 'lodash/flatten';
import { useCallback, useMemo } from "react";

import { useEnablementMapForSellingPlanId } from "@customer-app/state/reducers/sellingPlanGroups";

import { useCurrentBundle } from "../useCurrentBundle";

export const usePurchasablesForSubscribing = (
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>,
  inEditBundleModal?: boolean //TODO: Find a better way to override hideAllBundle items & hasBundle
) => {
  const { purchaseState } = delivery;
  const { bundle, allBundles, hideAllBundleItems, allBundleProductIds } = useCurrentBundle(delivery);

  const enablementMaps = useEnablementMapForSellingPlanId(purchaseState.sellingPlanId);

  const hasBundle = useMemo(
    () => !!bundle || purchaseState.containsBundle,
    [purchaseState.containsBundle, bundle]
  );

  return useCallback(
    (
      selectedDelivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState> | undefined,
      selectedPurchasables: IPurchasable[]
    ) => {
      const bundleParentInDelivery = getBundleParentFromLineItems(
        selectedDelivery?.purchaseState.stLineItems ?? []
      );

      return selectedPurchasables.filter(purchasable => {
        if (
          !purchasable.shopifyId ||
          !purchasable.isActiveInShopify ||
          !purchasable.vnts?.filter(vnt => vnt.isActiveInShopify).length ||
          purchasable.isDraftOrArchived
        ) {
          return false;
        }

        if (filterBundleParentsFromPurchasables(allBundles, purchasable)) {
          return false;
        }

        if (
          /**
           * If editing a bundle, we don't need to do any additional filtering.
           * That is done within the modal where we're comparing purchasables against collection data
           */
          !inEditBundleModal &&
          (hasBundle || hideAllBundleItems) &&
          allBundleProductIds.includes(purchasable.shopifyId)
        ) {
          return false;
        }

        if (
          hasBundle &&
          bundleParentInDelivery &&
          purchasable.vnts.filter(vnt => vnt.id === bundleParentInDelivery.vnt.id).length
        ) {
          return false;
        }

        const areAnyVariantsInStock = purchasable.vnts?.find(vnt => isVariantInStock(vnt));
        if (!areAnyVariantsInStock) {
          return false;
        }

        const purchasableVariantIds = flatten(
          purchasable.vnts?.reduce((vnt, curr) => {
            if (curr.shopifyId) {
              return vnt.concat(shopifyGidToNumber(curr.shopifyId));
            }
            return vnt;
          }, [] as number[])
        );

        if (
          !enablementMaps?.sellingPlanGroupWithEnablementMap?.productIds ||
          !enablementMaps.sellingPlanGroupWithEnablementMap.variantIds
        ) {
          return false;
        }

        const { productIds, variantIds } = enablementMaps.sellingPlanGroupWithEnablementMap;

        return (
          productIds.some(id => !!purchasable.shopifyId && id === shopifyGidToNumber(purchasable.shopifyId)) ||
          purchasableVariantIds.some(vntId => variantIds.includes(vntId))
        );
      });
    },
    [allBundles, enablementMaps, delivery]
  );
};
